const STORAGE_KEY = "tavela.profileName";
export const MAX_PROFILE_NAME_LENGTH = 18;
const PROFILE_NAME_VALIDATION_ERROR = "That profile name isn't allowed.";
export const PROFILE_NAME_REQUIRED_ERROR = "Profile name cannot be blank";
const PROFILE_NAME_CHARACTER_SUBSTITUTIONS: Record<string, string> = {
  "0": "o",
  "1": "i",
  "3": "e",
  "4": "a",
  "5": "s",
  "7": "t",
  "@": "a",
  "$": "s",
  "!": "i"
};
// Keep this list intentionally narrow to block obvious profanity without
// overmatching common names.
const BLOCKED_PROFILE_NAME_PATTERNS = [
  /^(?:mother)?fuck(?:er|ers|ing|ings|boy|boys|face|faces|head|heads|wad|wads)?$/,
  /^shit(?:ty|tys|head|heads|bag|bags|face|faces)?$/,
  /^bitch(?:es|y)?$/,
  /^cunt(?:s|y)?$/,
  /^asshole(?:s)?$/,
  /^slut(?:s|ty)?$/,
  /^whore(?:s)?$/,
  /^pussy(?:s)?$/,
  /^penis(?:es)?$/,
  /^vagina(?:s|l)?$/,
  /^nigg(?:er|ers|a|as)$/,
  /^fagg(?:ot|ots)$/,
  /^rape(?:r|rs|ist|ists)?$/
] as const;

type ProfileNameValidationOptions = {
  requireNonBlank?: boolean;
};

type SaveProfileNameOptions = ProfileNameValidationOptions & {
  currentProfileName?: unknown;
};

function getLocalStorage(): Storage | null {
  if (typeof window === "undefined") {
    return null;
  }

  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

export function sanitizeProfileName(value: unknown): string {
  if (typeof value !== "string") {
    return "";
  }

  return value.replace(/\s+/g, " ").trim().slice(0, MAX_PROFILE_NAME_LENGTH);
}

function normalizeProfileNameForModeration(value: string): string {
  return value.toLowerCase().replace(/[013457@$!]/g, (character) => {
    return PROFILE_NAME_CHARACTER_SUBSTITUTIONS[character] ?? character;
  });
}

function getProfileNameModerationCandidates(value: string): string[] {
  const normalizedValue = normalizeProfileNameForModeration(value);
  const tokenCandidates = normalizedValue.split(/[^a-z]+/).filter((token) => token.length > 0);
  const collapsedCandidate = normalizedValue.replace(/[^a-z]+/g, "");

  if (collapsedCandidate.length === 0 || tokenCandidates.includes(collapsedCandidate)) {
    return tokenCandidates;
  }

  return [...tokenCandidates, collapsedCandidate];
}

function isBlockedProfileName(value: string): boolean {
  const candidates = getProfileNameModerationCandidates(value);

  return candidates.some((candidate) =>
    BLOCKED_PROFILE_NAME_PATTERNS.some((pattern) => pattern.test(candidate))
  );
}

export function getProfileNameValidationError(
  value: unknown,
  options: ProfileNameValidationOptions = {}
): string | null {
  const sanitizedValue = sanitizeProfileName(value);
  if (sanitizedValue.length === 0) {
    return options.requireNonBlank === true ? PROFILE_NAME_REQUIRED_ERROR : null;
  }

  return isBlockedProfileName(sanitizedValue) ? PROFILE_NAME_VALIDATION_ERROR : null;
}

export function sanitizeProfileNameForSave(
  value: unknown,
  options: ProfileNameValidationOptions = {}
): string {
  const sanitizedValue = sanitizeProfileName(value);

  return getProfileNameValidationError(sanitizedValue, options) === null ? sanitizedValue : "";
}

export function loadProfileName(): string {
  const storage = getLocalStorage();
  if (storage === null) {
    return "";
  }

  try {
    const storedValue = storage.getItem(STORAGE_KEY);
    if (storedValue === null) {
      return "";
    }

    return sanitizeProfileNameForSave(JSON.parse(storedValue));
  } catch {
    return "";
  }
}

export function saveProfileName(
  profileName: string,
  options: SaveProfileNameOptions = {}
): string {
  const currentProfileName = sanitizeProfileName(options.currentProfileName);
  const requireNonBlank =
    options.requireNonBlank ?? currentProfileName.length > 0;
  const nextProfileName = sanitizeProfileNameForSave(profileName, {
    requireNonBlank
  });
  const profileNameToStore =
    nextProfileName.length > 0 || !requireNonBlank ? nextProfileName : currentProfileName;
  const storage = getLocalStorage();
  if (storage === null) {
    return profileNameToStore;
  }

  try {
    storage.setItem(STORAGE_KEY, JSON.stringify(profileNameToStore));
  } catch {
    // Ignore storage write failures so the app stays playable.
  }

  return profileNameToStore;
}
